
import { motion } from "framer-motion";
import { Truck, Shield, RotateCcw, Clock, Gift, Users } from "lucide-react";

const badges = [
  {
    icon: Truck,
    title: "Free Shipping",
    description: "On prepaid orders above ₹500",
    color: "from-blue-500 to-cyan-500",
  },
  {
    icon: Clock,
    title: "24-Hour Delivery",
    description: "Express delivery in Delhi NCR",
    color: "from-purple-500 to-pink-500",
  },
  {
    icon: RotateCcw,
    title: "Easy Returns",
    description: "Quick exchanges & returns",
    color: "from-green-500 to-emerald-500",
  },
  {
    icon: Shield,
    title: "Premium Quality",
    description: "Authentic stones & handwork",
    color: "from-amber-500 to-orange-500",
  },
  {
    icon: Gift,
    title: "COD Available",
    description: "Cash on delivery option",
    color: "from-pink-500 to-rose-500",
  },
  {
    icon: Users,
    title: "20K+ Customers",
    description: "Trusted worldwide",
    color: "from-indigo-500 to-purple-500",
  },
];

export default function TrustBadges() {
  return (
    <section id="why-aarsa" className="py-12 bg-gradient-to-b from-white via-gray-50/50 to-white relative overflow-hidden mt-4 mb-4 scroll-mt-24">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-96 h-96 bg-purple-100/30 rounded-full blur-3xl"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold font-playfair mb-3">
            Why Choose <span className="gradient-text">Aarsa</span>
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Experience the difference with our commitment to quality, service, and your satisfaction
          </p>
        </motion.div>

        {/* Horizontal Flow Layout */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-4">
          {badges.map((badge, index) => {
            const Icon = badge.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="flex flex-col items-center text-center group flex-1 max-w-xs"
              >
                {/* Icon with Gradient Background */}
                <motion.div
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  className={`relative mb-4 w-16 h-16 rounded-full bg-gradient-to-br ${badge.color} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300`}
                >
                  <Icon className="w-8 h-8 text-white" />
                  {/* Glow effect on hover */}
                  <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${badge.color} opacity-0 group-hover:opacity-50 blur-xl transition-opacity duration-300`} />
                </motion.div>

                {/* Content */}
                <h3 className="font-bold text-gray-900 mb-1.5 text-base">
                  {badge.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {badge.description}
                </p>

                {/* Decorative Line */}
                <motion.div
                  className={`mt-4 w-12 h-0.5 bg-gradient-to-r ${badge.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
                  whileHover={{ width: 24 }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Bottom Accent Line */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.5 }}
          className="mt-8 h-1 bg-gradient-to-r from-transparent via-purple-300 to-transparent rounded-full"
        />
      </div>
    </section>
  );
}
